import { Injectable } from '@angular/core';
import { Kpis } from '../states/dashboard-state';
import { StatisticsType } from '../../../../../redux/models/statistics/statistics-type';
import { KpiDataPoint, KpiSummaryData } from '../../../../../redux/models/statistics/kpi-summary-data';
import { of } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectCurrentKpis, selectPreviousKpis } from '../selectors/dashboard-selector';
import { StatisticsFilter } from '../../../../../redux/models/statistics/statistics-filter';

@Injectable({ providedIn: 'root' })
export class KpiSummaryTransformerService {
  constructor(private readonly store: Store) {}

  transformKpi(statisticsType: StatisticsType, statisticsFilter: StatisticsFilter, currency: string) {
    const currentKpiState = this.currentKpisState();
    let previousKpiState = null;

    const NEED_YOY = statisticsFilter.comparePeriodYearOffset === 1;
    if (NEED_YOY) {
      previousKpiState = this.previousKpisState();
    }

    return of(this.transformKpis(statisticsType, currentKpiState, previousKpiState, currency, NEED_YOY));
  }

  transformKpis(
    statisticsType: StatisticsType,
    currentKpi: Kpis,
    previousKpi: Kpis,
    currencyValue: string,
    needYoy: boolean
  ) {
    const kpiSummaryData: KpiSummaryData[] = [];
    switch (statisticsType) {
      case StatisticsType.GROSS_SALES: {
        let totalGrossSalesYoy = null;
        let averageGrossSalesYoy = null;
        if (needYoy) {
          totalGrossSalesYoy = this.isWhatPercentOf(currentKpi?.totalGrossSales, previousKpi?.totalGrossSales);
          averageGrossSalesYoy = this.isWhatPercentOf(currentKpi?.averageGrossSales, previousKpi?.averageGrossSales);
        }
        kpiSummaryData.push({
          type: KpiDataPoint.TOTAL_GROSS_SALES_REVENUE,
          value: currentKpi?.totalGrossSales,
          yoy: totalGrossSalesYoy,
          currency: currencyValue
        });
        kpiSummaryData.push({
          type: KpiDataPoint.AVERAGE_BOOKING_VALUE,
          value: currentKpi?.averageGrossSales,
          yoy: averageGrossSalesYoy,
          currency: currencyValue
        });

        break;
      }

      case StatisticsType.NET_REVENUES: {
        let totalNetRevenuesYoy = null;
        if (needYoy) {
          totalNetRevenuesYoy = this.isWhatPercentOf(currentKpi?.totalNetRevenues, previousKpi?.totalNetRevenues);
        }
        kpiSummaryData.push({
          type: KpiDataPoint.TOTAL_NET_REVENUE,
          value: currentKpi?.totalNetRevenues,
          yoy: totalNetRevenuesYoy,
          currency: currencyValue
        });
        break;
      }
      case StatisticsType.NUMBER_OF_BOOKINGS: {
        let totalBookingsYoy = null;
        let totalLengthOfStayYoy = null;
        let averageLengthOfStayYoy = null;
        let averageLeadTimeYoy = null;
        if (needYoy) {
          totalBookingsYoy = this.isWhatPercentOf(currentKpi?.totalBookings, previousKpi?.totalBookings);
          totalLengthOfStayYoy = this.isWhatPercentOf(currentKpi?.totalLengthOfStay, previousKpi?.totalLengthOfStay);
          averageLengthOfStayYoy = this.isWhatPercentOf(
            currentKpi?.averageLengthOfStay,
            previousKpi?.averageLengthOfStay
          );
          averageLeadTimeYoy = this.isWhatPercentOf(currentKpi?.averageLeadTime, previousKpi?.averageLeadTime);
        }
        kpiSummaryData.push({
          type: KpiDataPoint.TOTAL_BOOKINGS,
          value: currentKpi?.totalBookings,
          yoy: totalBookingsYoy
        });
        kpiSummaryData.push({
          type: KpiDataPoint.TOTAL_NIGHTS,
          value: currentKpi?.totalLengthOfStay,
          yoy: totalLengthOfStayYoy
        });
        kpiSummaryData.push({
          type: KpiDataPoint.AVERAGE_LENGTH_OF_STAY_IN_NIGHTS,
          value: currentKpi?.averageLengthOfStay,
          yoy: averageLengthOfStayYoy
        });
        kpiSummaryData.push({
          type: KpiDataPoint.AVERAGE_LEAD_TIME_IN_DAYS,
          value: currentKpi?.averageLeadTime,
          yoy: averageLeadTimeYoy
        });
        break;
      }
      case StatisticsType.REVENUES: {
        let totalRevenueYoy = null;
        let averageRevenuesYoy = null;
        if (needYoy) {
          totalRevenueYoy = this.isWhatPercentOf(currentKpi?.totalRevenue, previousKpi?.totalRevenue);
          averageRevenuesYoy = this.isWhatPercentOf(currentKpi?.averageRevenues, previousKpi?.averageRevenues);
        }
        kpiSummaryData.push({
          type: KpiDataPoint.TOTAL_REVENUE,
          value: currentKpi?.totalRevenue,
          yoy: totalRevenueYoy,
          currency: currencyValue
        });
        kpiSummaryData.push({
          type: KpiDataPoint.AVERAGE_MARK_UP,
          value: currentKpi?.averageRevenues,
          currency: currencyValue,
          yoy: averageRevenuesYoy
        });
        break;
      }
      default:
        break;
    }
    return kpiSummaryData;
  }

  private isWhatPercentOf(value, yoyValue): number {
    if (!yoyValue) {
      return 100;
    }
    return yoyValue ? +(((value - yoyValue) / yoyValue) * 100).toFixed(2) : 0;
  }

  private currentKpisState() {
    let isLoading = false;
    let currentKpis: Kpis;
    this.store.select(selectCurrentKpis).subscribe(data => {
      currentKpis = data;
      isLoading = true;
    });
    if (isLoading) {
      return currentKpis;
    }
    return null;
  }

  private previousKpisState() {
    let isLoading = false;
    let previousKpis: Kpis;
    this.store.select(selectPreviousKpis).subscribe(data => {
      previousKpis = data;
      isLoading = true;
    });
    if (isLoading) {
      return previousKpis;
    }
    return null;
  }
}
